import { useEffect, useRef, useState } from 'react';
import { PortraitScene } from '../three/PortraitScene';
import { prefersReducedMotion } from '../three/state';
import { resolveTask } from '../lib/loader';

type ParticlePortraitProps = {
  src: string;
  alt: string;
  className?: string;
};

/**
 * WebGL particle portrait with a plain <img> fallback when WebGL is missing.
 * Built during the loader so it is ready before anyone scrolls to it.
 */
export function ParticlePortrait({
  src,
  alt,
  className = '',
}: ParticlePortraitProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let scene: PortraitScene;
    try {
      scene = new PortraitScene(canvas, prefersReducedMotion());
    } catch {
      setFailed(true);
      resolveTask('portrait');
      return;
    }
    scene
      .load(src)
      .catch(() => setFailed(true))
      .finally(() => resolveTask('portrait'));
    return () => scene.destroy();
  }, [src]);

  if (failed) {
    return (
      <img
        src={src}
        alt={alt}
        className={`${className} rounded-full object-cover`}
        loading="lazy"
      />
    );
  }

  return (
    <canvas
      ref={ref}
      role="img"
      aria-label={alt}
      className={`${className} touch-pan-y`}
    />
  );
}
